import { type ReactNode } from 'react';
import { type CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';

import { type BookmakersConfig } from '~/vendor/cx-modules/types';
import { type RequestContext } from '~/vendor/cx-web/domain-types';

import { type BettingInsightsResultAttribute, type EsIndex } from '../types';

// Type for the context that will be passed to render and getLabel functions
export interface BettingInsightsFilterContext<TIndex extends EsIndex = EsIndex> {
  attribute: BettingInsightsResultAttribute<TIndex>;
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  isWalled: boolean;
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsFilterConfiguration<TIndex extends EsIndex, TLabel = string> {
  attribute: BettingInsightsResultAttribute<TIndex>;
  getLabel: (context: BettingInsightsFilterContext<TIndex>) => TLabel;
  render: (
    context: BettingInsightsFilterContext<TIndex>,
    {
      bookmakers,
      currentRefinements,
    }: {
      bookmakers: BookmakersConfig;
      currentRefinements: CurrentRefinementsConnectorParamsItem[];
    },
  ) => ReactNode;
}

// Type alias for the return type of createBettingInsightsFilter
export type BettingInsightsFilter<TIndex extends EsIndex = EsIndex, TLabel = string> = ReturnType<
  typeof createBettingInsightsFilter<TIndex, TLabel>
>;

/**
 * Factory function to create BettingInsightsFilter configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The filter configuration
 * @returns Immutable filter configuration object
 */
export function createBettingInsightsFilter<TIndex extends EsIndex, TLabel = string>(
  requestContext: RequestContext,
  config: BettingInsightsFilterConfiguration<TIndex, TLabel>,
) {
  // Create context object that will be passed to methods
  const context: BettingInsightsFilterContext<TIndex> = {
    attribute: config.attribute,
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
    isWalled: requestContext.user.walls.length > 0,
  };

  // Create result object with wrapper methods that inject context as first parameter
  const result = {
    // Config property needed by components
    attribute: context.attribute,

    // Wrapped methods that inject context
    getLabel: () => config.getLabel(context),
    render: ({
      bookmakers,
      currentRefinements,
    }: {
      bookmakers: BookmakersConfig;
      currentRefinements: CurrentRefinementsConnectorParamsItem[];
    }) => config.render(context, { bookmakers, currentRefinements }),
  };

  return Object.freeze(result);
}
